import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, FileText, BookOpen, Download, AlertTriangle, ExternalLink, ListChecks } from "lucide-react";

/**
 * Card encouraging the user to share preparation materials with a trusted family
 * member or friend. The family fills the forms in calm (with the user's data),
 * but signature and date are reserved for the user on the day of the hearing.
 */
export default function FamilyHelpCard() {
  const navigate = useNavigate();

  return (
    <section
      data-testid="dashboard-family-help"
      className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-indigo-700 flex items-center justify-center">
          <Users className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-black text-indigo-900">
            Información para que un familiar o amigo le ayude
          </p>
          <p className="text-xs text-indigo-700">Preparen el formulario juntos, con calma</p>
        </div>
      </div>

      <p className="text-sm text-zinc-800 leading-snug mb-4">
        Su familia o un amigo de confianza, por estar afuera, tienen <span className="font-bold">más calma, tiempo e internet</span> que usted ahora. Pueden ayudarle preparando los formularios desde su casa, basándose en sus datos. Comparta los siguientes materiales con esa persona de confianza.
      </p>

      {/* Materiales oficiales para descargar */}
      <div className="bg-white border-2 border-indigo-100 rounded-xl p-4 mb-4">
        <p className="text-xs font-black uppercase tracking-wider text-indigo-900 mb-3 flex items-center gap-1.5">
          <Download className="w-4 h-4" />
          Materiales oficiales para descargar e imprimir
        </p>
        <ul className="space-y-2.5">
          <DownloadLink
            testId="family-link-i589"
            href="https://www.uscis.gov/i-589"
            Icon={FileText}
            title="Formulario I-589 oficial (USCIS)"
            desc="Solicitud de Asilo y Suspensión de Remoción · PDF gratuito del gobierno"
          />
          <DownloadLink
            testId="family-link-florence"
            href="https://firrp.org/resources/prose/"
            Icon={BookOpen}
            title="Manual del Proyecto Florence (Pro Se)"
            desc="Guía paso a paso para auto-representación · disponible en español"
          />
          <DownloadLink
            testId="family-link-nijc"
            href="https://immigrantjustice.org/asylum-seekers-detention-self-help-guide"
            Icon={BookOpen}
            title="Self-Help Guide del NIJC"
            desc="National Immigrant Justice Center · guía para solicitantes en detención"
          />
          <button
            data-testid="family-link-glossary"
            onClick={() => navigate("/glosario-i589")}
            className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-indigo-50 transition-colors group"
          >
            <div className="w-9 h-9 rounded-lg bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center flex-shrink-0">
              <ListChecks className="w-4 h-4 text-emerald-700" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-zinc-900 truncate">
                Glosario miapoyo del I-589
              </p>
              <p className="text-xs text-zinc-600">
                Casilla por casilla, en español + qué escribir en inglés · imprimible
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-indigo-700 flex-shrink-0" />
          </button>
        </ul>
      </div>

      {/* Instrucciones para el familiar */}
      <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 mb-4">
        <p className="text-xs font-black uppercase tracking-wider text-emerald-800 mb-3">
          Cómo deben hacerlo entre ustedes dos
        </p>
        <ol className="space-y-2.5">
          <InstructionStep n={1}>
            Su familiar <span className="font-bold">descarga e imprime</span> el formulario I-589 desde el enlace de USCIS.
          </InstructionStep>
          <InstructionStep n={2}>
            Descargan también el <span className="font-bold">glosario de miapoyo</span> para tenerlo de referencia mientras llenan cada casilla.
          </InstructionStep>
          <InstructionStep n={3}>
            Su familiar le pregunta sus datos personales en una llamada y los va anotando en una hoja aparte: nombre completo, fecha de nacimiento, ciudad y país de nacimiento, dirección del centro de detención, etcétera.
          </InstructionStep>
          <InstructionStep n={4}>
            Con calma, su familiar llena el formulario impreso <span className="font-bold">en MAYÚSCULAS y en inglés</span>, siguiendo el glosario casilla por casilla.
          </InstructionStep>
          <InstructionStep n={5}>
            Para la sección del relato de miedo (Parte B), su familiar puede transcribir lo que usted le cuenta en sus llamadas. Llámenos desde el centro de detención si tienen dudas.
          </InstructionStep>
        </ol>
      </div>

      {/* ADVERTENCIAS LEGALES CRÍTICAS */}
      <div
        data-testid="family-help-warning"
        className="bg-red-50 border-2 border-red-300 rounded-xl p-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0" />
          <p className="text-sm font-black text-red-900 uppercase tracking-wider">
            Reglas que NO se pueden romper
          </p>
        </div>
        <ul className="space-y-2 text-xs text-zinc-800 leading-snug">
          <Rule>
            El formulario debe ser firmado <span className="font-bold">por usted</span>, el solicitante, NUNCA por su familiar. Su familiar solo llena los datos.
          </Rule>
          <Rule>
            <span className="font-bold">NO firme ni ponga la fecha todavía.</span> Deje esas dos casillas EN BLANCO. Se firman y fechan el día de su audiencia ante el juez de inmigración.
          </Rule>
          <Rule>
            Su familiar <span className="font-bold">no puede llenar por usted la Parte B</span> (relato del miedo) con palabras suyas. Solo puede transcribir lo que usted le diga textualmente. Si su familiar inventa o exagera hechos, el caso se rechaza.
          </Rule>
          <Rule>
            Todo el formulario va <span className="font-bold">en inglés y en MAYÚSCULAS</span>. Si una casilla no aplica, escriban la palabra NONE, no la dejen en blanco.
          </Rule>
          <Rule>
            Documentos en otro idioma (acta de nacimiento, denuncias policiales, certificados médicos) requieren <span className="font-bold">traducción certificada al inglés</span> antes de presentarse.
          </Rule>
          <Rule>
            <span className="font-bold">Ni miapoyo ni su familiar son abogados.</span> Esto es auto-ayuda legal autorizada por la ley federal (PBNDS 2011, sección 6.3). Si puede contratar un abogado pro bono, hágalo.
          </Rule>
        </ul>
      </div>
    </section>
  );
}

function DownloadLink({ testId, href, Icon, title, desc }) {
  return (
    <li>
      <a
        data-testid={testId}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-indigo-50 transition-colors group"
      >
        <div className="w-9 h-9 rounded-lg bg-indigo-100 group-hover:bg-indigo-200 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-indigo-700" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-zinc-900 truncate">{title}</p>
          <p className="text-xs text-zinc-600 truncate">{desc}</p>
        </div>
        <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-indigo-700 flex-shrink-0" />
      </a>
    </li>
  );
}

function InstructionStep({ n, children }) {
  return (
    <li className="flex gap-2.5">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-700 text-white flex items-center justify-center font-black text-xs">
        {n}
      </div>
      <p className="text-xs text-zinc-800 leading-snug pt-0.5">{children}</p>
    </li>
  );
}

function Rule({ children }) {
  return (
    <li className="flex gap-2 leading-snug">
      <span className="text-red-700 font-black flex-shrink-0">•</span>
      <span>{children}</span>
    </li>
  );
}
